/**
 * ManualEntryForm Component
 * 
 * Form for manually entering email and request code.
 * Task: T066, T068 [US2]
 * 
 * Features:
 * - Email and request code input fields
 * - Client-side validation (HTML5 + custom)
 * - Loading states during submission
 * - Pre-filled values support
 * - Generic error messages (T070)
 */

import React, { useState } from 'react';
import { Card, CardBody, Input, Button, Typography } from '@material-tailwind/react';
import { validateEmail, validateGuid } from '../utils/validators';

const ManualEntryForm = ({ 
  onSubmit, 
  isLoading = false,
  initialEmail = '',
  initialRequestCode = '',
  error = null
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [requestCode, setRequestCode] = useState(initialRequestCode);
  const [emailError, setEmailError] = useState('');
  const [requestCodeError, setRequestCodeError] = useState('');

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError(''); // Clear error on change
  };

  const handleRequestCodeChange = (e) => {
    setRequestCode(e.target.value);
    if (requestCodeError) setRequestCodeError(''); // Clear error on change
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Reset errors
    setEmailError('');
    setRequestCodeError('');

    // Validate both fields so all errors are surfaced at once
    const emailValidation = validateEmail(email);
    const guidValidation = validateGuid(requestCode);

    let hasError = false;
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error || 'Invalid email format');
      hasError = true;
    }
    if (!guidValidation.valid) {
      setRequestCodeError(guidValidation.error || 'Invalid request code format');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    // Submit with sanitized values
    onSubmit(emailValidation.email, guidValidation.guid);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardBody>
        <Typography variant="h5" color="blue-gray" className="mb-2">
          Enter Payment Request Details
        </Typography>
        <Typography variant="small" color="blue-gray" className="mb-6 opacity-70">
          Please enter your email address and request code to continue
        </Typography>

        <form onSubmit={handleSubmit} noValidate>
          {/* Email Input */}
          <div className="mb-6">
            <Input
              type="email"
              label="Email Address"
              aria-label="Email Address"
              value={email}
              onChange={handleEmailChange}
              disabled={isLoading}
              error={!!emailError}
              required
              className="!border-t-blue-gray-200 focus:!border-t-gray-900"
            />
            {emailError && (
              <Typography variant="small" color="red" className="mt-2">
                {emailError}
              </Typography>
            )}
          </div>

          {/* Request Code Input */}
          <div className="mb-6">
            <Input
              type="text"
              label="Request Code"
              aria-label="Request Code"
              value={requestCode}
              onChange={handleRequestCodeChange}
              disabled={isLoading}
              error={!!requestCodeError}
              required
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="!border-t-blue-gray-200 focus:!border-t-gray-900"
            />
            {requestCodeError && (
              <Typography variant="small" color="red" className="mt-2">
                {requestCodeError}
              </Typography>
            )}
            <Typography variant="small" color="blue-gray" className="mt-2 opacity-60">
              Your request code was provided in the payment request email
            </Typography>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            size="lg"
            className="w-full bg-fotl-purple hover:bg-fotl-purple-dark"
            disabled={isLoading}
          >
            {isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </form>

        <Typography variant="small" color="blue-gray" className="mt-4 text-center opacity-60">
          Don't have a request code? Contact support for assistance.
        </Typography>
      </CardBody>
    </Card>
  );
};

export default ManualEntryForm;
